<template>
	<div class="cms-pdf-viewer-block">
		<!-- Iframe-based embed — works for direct PDF URLs and Google Docs proxy -->
		<iframe
			v-if="resolvedSrc"
			class="cms-pdf-viewer-block__frame"
			:src="resolvedSrc"
			:style="frameStyle"
			frameborder="0"
			allowfullscreen
			:title="title || 'PDF Viewer'"
		></iframe>

		<!-- Empty state when no URL is set or URL is invalid for Google Viewer -->
		<div v-else class="cms-pdf-viewer-block__empty">
			<i class="fa fa-file-pdf-o"></i>
			<span v-if="!src">No PDF URL configured.</span>
			<span v-else>PDF URL must start with http:// or https://</span>
		</div>
	</div>
</template>

<script>
/**
 * PdfViewerBlock.vue
 * ──────────────────
 * Embeds a PDF document using an <iframe>. The PDF URL is either a direct
 * link (served with the correct Content-Type) or is proxied through the
 * Google Docs viewer for cross-browser compatibility.
 *
 * Modes
 *   direct  — renders the URL as-is (requires the server to send
 *              Content-Type: application/pdf or the browser will download it).
 *   google  — wraps the URL in the Google Docs viewer for maximum
 *              browser compatibility with remote PDFs.
 *
 * Width, alignment, padding, border and visibility are wrapper traits.
 * The root is width:100% and the iframe inherits the wrapper's radius.
 */
export default {
	name: 'PdfViewerBlock',

	props: {
		/** Public URL of the PDF file */
		src:  { type: String, default: '' },

		/** Accessibility title shown as the iframe title attribute */
		title: { type: String, default: '' },

		/** Height of the viewer iframe */
		height: { type: String, default: '600px' },

		/**
		 * 'direct' — embed raw PDF URL.
		 * 'google' — proxy via Google Docs Viewer (better compatibility with
		 *             remote PDFs that don't set Content-Disposition: inline).
		 */
		embedMode: {
			type: String,
			default: 'google',
			validator: function (v) { return ['direct', 'google'].indexOf(v) !== -1; }
		}
	},

	computed: {
		resolvedSrc: function () {
			if (!this.src) return '';
			// Only construct the Google Docs viewer URL for absolute http/https URLs
			// to avoid exposing relative paths or file:// URIs to the external viewer.
			var isAbsolute = /^https?:\/\//i.test(this.src);
			if (this.embedMode === 'google') {
				if (!isAbsolute) return '';
				return 'https://docs.google.com/viewer?url=' + encodeURIComponent(this.src) + '&embedded=true';
			}
			return this.src;
		},

		frameStyle: function () {
			return {
				display: 'block',
				width: '100%',
				height: this.height,
				border: 'none',
				borderRadius: 'inherit',
				background: '#F9F9F9'
			};
		}
	}
};
</script>

<style scoped>
.cms-pdf-viewer-block { width: 100%; max-width: 100%; border-radius: inherit; }

.cms-pdf-viewer-block__empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	min-height: 120px;
	color: #b0b5bc;
	border: 2px dashed #E9E9EA;
	border-radius: 6px;
	font-size: 1em;
}

.cms-pdf-viewer-block__empty .fa {
	font-size: 2rem;
	opacity: 0.5;
}
</style>
